/**
 * Inspector "Animation" section (timeline open): every animatable property of the selected element with its value
 * at the playhead in the open phase. A diamond adds or removes the keyframe at the playhead; editing a value sets
 * a keyframe there (creating the track), so values are edited where the operator sees them.
 */
import type { AnimatableProperty, Element } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';
import { animatableFor, findTrack, getPhase, phaseKey, removeKeyAt, setKeyAt, toHexColor, valueAt, type PhaseRef } from '../model/timeline.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { ColorField, NumberField, Section } from '../ui/controls.tsx';
import { phaseLabel } from './TimelinePanel.tsx';

const SHORT: Record<AnimatableProperty, string> = {
  x: 'X', y: 'Y', scaleX: 'SX', scaleY: 'SY', rotation: '°', opacity: 'α', width: 'W', height: 'H',
  insetTop: 'Clip T', insetRight: 'Clip R', insetBottom: 'Clip B', insetLeft: 'Clip L', color: '', blur: 'Blur', letterSpacing: 'Track',
};

const STEP: Partial<Record<AnimatableProperty, number>> = { opacity: 0.05, scaleX: 0.05, scaleY: 0.05 };

export function AnimationSection({ store, el }: { store: EditorStore; el: Element }) {
  const scene = useEditor(store, sceneView);
  const anim = useEditor(store, (s) => s.anim);
  if (anim.target.kind === 'through') return null;
  const ref = anim.target as PhaseRef;
  const phase = getPhase(scene, ref);
  if (!phase) return null;
  const time = anim.time;
  return (
    <Section title={t('anim.section', { phase: phaseLabel(scene, ref) })} testId="section-animation">
      <p className="hint">{t('anim.hint')}</p>
      <div className="anim-props">
        {animatableFor(el).map((p) => {
          const ti = findTrack(phase, el.id, p);
          const track = ti >= 0 ? phase.tracks[ti] : undefined;
          const keyed = track?.keyframes.some((k) => Math.abs(k.time - time) < 0.5) ?? false;
          const v = valueAt(scene, phase, el.id, p, time);
          const set = (value: number | string) => store.apply((s) => setKeyAt(s, ref, el.id, p, time, value), { key: `anim:${phaseKey(ref)}:${el.id}:${p}:${time}` });
          return (
            <div key={p} className={`anim-prop${track ? ' animated' : ''}`} data-testid={`anim-${p}`}>
              <button
                type="button"
                className={`key-toggle${keyed ? ' on' : track ? ' track' : ''}`}
                aria-pressed={keyed}
                aria-label={keyed ? t('anim.removeKey', { property: t(`anim.prop.${p}`) }) : t('anim.addKey', { property: t(`anim.prop.${p}`) })}
                title={keyed ? t('anim.removeKey', { property: t(`anim.prop.${p}`) }) : t('anim.addKey', { property: t(`anim.prop.${p}`) })}
                data-testid={`key-toggle-${p}`}
                onClick={() => store.apply((s) => (keyed ? removeKeyAt(s, ref, el.id, p, time) : setKeyAt(s, ref, el.id, p, time, valueAt(s, getPhase(s, ref), el.id, p, time))))}
              >
                ◆
              </button>
              {typeof v === 'string' ? (
                <ColorField label={t(`anim.prop.${p}`)} value={toHexColor(v)} onChange={set} testId={`anim-value-${p}`} />
              ) : (
                <NumberField label={t(`anim.prop.${p}`)} value={Math.round(v * 1000) / 1000} short={SHORT[p]} step={STEP[p] ?? 1} onChange={set} testId={`anim-value-${p}`} />
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
